import { toast } from "sonner";
import type { PrintJob } from "@/lib/qz-tray";

const QUEUE_STORAGE_KEY = "qz_print_queue";
const STALE_ALERT_KEY = "qz_stale_alert_sent";
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export interface PendingPrintJob extends PrintJob {
  id: string;
  createdAt: string;
  orderNumber?: number;
  attempts: number;
  lastError?: string;
}

// Get pending jobs from localStorage
export function getPendingPrintJobs(): PendingPrintJob[] {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

// Save pending jobs to localStorage
function savePendingJobs(jobs: PendingPrintJob[]): void {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(jobs));
  } catch (e) {
    console.error("[PrintQueue] Error saving queue:", e);
  }
}

// Add a job to the pending queue
export function addToPrintQueue(
  job: PrintJob,
  orderNumber?: number,
  errorMessage?: string
): void {
  const pendingJob: PendingPrintJob = {
    ...job,
    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    orderNumber,
    attempts: 0,
    lastError: errorMessage,
  };

  const currentQueue = getPendingPrintJobs();
  currentQueue.push(pendingJob);
  savePendingJobs(currentQueue);
  
  console.log(`[PrintQueue] Job added to queue: ${pendingJob.id}`);
}

// Add multiple jobs to the queue
export function addMultipleToPrintQueue(
  jobs: PrintJob[],
  orderNumber?: number,
  errorMessage?: string
): void {
  const currentQueue = getPendingPrintJobs();
  
  for (const job of jobs) {
    const pendingJob: PendingPrintJob = {
      ...job,
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      orderNumber,
      attempts: 0,
      lastError: errorMessage,
    };
    currentQueue.push(pendingJob);
  }
  
  savePendingJobs(currentQueue);
  console.log(`[PrintQueue] ${jobs.length} jobs added to queue`);
}

// Remove a job from the queue (after successful print)
export function removeFromPrintQueue(jobId: string): void {
  const currentQueue = getPendingPrintJobs();
  const filtered = currentQueue.filter(job => job.id !== jobId);
  savePendingJobs(filtered);
  console.log(`[PrintQueue] Job removed: ${jobId}`);
}

// Update job attempt count and error
export function updatePrintJobAttempt(jobId: string, error?: string): void {
  const currentQueue = getPendingPrintJobs();
  const jobIndex = currentQueue.findIndex(job => job.id === jobId);
  
  if (jobIndex >= 0) {
    currentQueue[jobIndex].attempts += 1;
    currentQueue[jobIndex].lastError = error;
    savePendingJobs(currentQueue);
  }
}

// Clear all pending jobs
export function clearPrintQueue(): void {
  localStorage.removeItem(QUEUE_STORAGE_KEY);
  localStorage.removeItem(STALE_ALERT_KEY);
  console.log("[PrintQueue] Queue cleared");
}

// Get queue count
export function getPendingPrintCount(): number {
  return getPendingPrintJobs().length;
}

// Check for stale jobs (older than 5 minutes)
export function getStalePrintJobs(): PendingPrintJob[] {
  const jobs = getPendingPrintJobs();
  const now = Date.now();
  
  return jobs.filter(job => {
    const createdAt = new Date(job.createdAt).getTime();
    return (now - createdAt) >= STALE_THRESHOLD_MS;
  });
}

// Check if stale alert was already sent (to avoid spamming)
function wasStaleAlertSent(): boolean {
  const lastAlert = localStorage.getItem(STALE_ALERT_KEY);
  if (!lastAlert) return false;
  
  // Reset after 30 minutes
  const lastAlertTime = parseInt(lastAlert, 10);
  return (Date.now() - lastAlertTime) < 30 * 60 * 1000;
}

// Mark stale alert as sent
function markStaleAlertSent(): void {
  localStorage.setItem(STALE_ALERT_KEY, Date.now().toString());
}

// Reset stale alert (when queue is cleared or prints succeed)
export function resetStaleAlert(): void {
  localStorage.removeItem(STALE_ALERT_KEY);
}

// Check for stale jobs and send push notification if needed
export async function checkAndNotifyStalePrintJobs(
  restaurantId: string
): Promise<boolean> {
  const staleJobs = getStalePrintJobs();
  
  if (staleJobs.length === 0) {
    return false;
  }
  
  if (wasStaleAlertSent()) {
    console.log("[PrintQueue] Stale alert already sent recently, skipping");
    return false;
  }

  console.log(`[PrintQueue] ${staleJobs.length} stale print jobs found, sending notification`);
  
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    
    await supabase.functions.invoke("send-push-notification", {
      body: {
        restaurant_id: restaurantId,
        notification_type: "stale_prints",
        stale_count: staleJobs.length,
        oldest_job_time: staleJobs[0]?.createdAt,
      },
    });
    
    markStaleAlertSent();
    
    // Also show a local toast warning
    toast.warning(`${staleJobs.length} impressão(ões) pendente(s) há mais de 5 min`, {
      description: "Verifique a conexão com a impressora",
      duration: 10000,
    });
    
    return true;
  } catch (error) {
    console.error("[PrintQueue] Error sending stale notification:", error);
    return false;
  }
}

// Process all pending jobs (called when QZ reconnects)
export async function processPendingPrintJobs(): Promise<{
  success: number;
  failed: number;
}> {
  const pendingJobs = getPendingPrintJobs();
  
  if (pendingJobs.length === 0) {
    return { success: 0, failed: 0 };
  }

  console.log(`[PrintQueue] Processing ${pendingJobs.length} pending jobs...`);
  
  // Import dynamically to avoid circular dependency
  const { printReceipt, isQZConnected } = await import("@/lib/qz-tray");
  
  if (!isQZConnected()) {
    console.log("[PrintQueue] QZ not connected, skipping processing");
    return { success: 0, failed: pendingJobs.length };
  }

  let successCount = 0;
  let failedCount = 0;

  for (const job of pendingJobs) {
    try {
      const printed = await printReceipt(job.printerName, job.content, {
        skipLog: false,
        logType: job.logType,
        logIdentifier: job.logIdentifier,
        locationName: job.locationName,
      });

      if (printed) {
        removeFromPrintQueue(job.id);
        successCount++;
      } else {
        updatePrintJobAttempt(job.id, "Falha na impressão");
        failedCount++;
      }
    } catch (error: any) {
      updatePrintJobAttempt(job.id, error.message);
      failedCount++;
    }
  }

  if (successCount > 0) {
    // Reset stale alert when prints succeed
    resetStaleAlert();
    
    toast.success(`${successCount} impressão(ões) pendente(s) enviada(s)`, {
      description: failedCount > 0 ? `${failedCount} ainda pendente(s)` : undefined,
    });
  }

  console.log(`[PrintQueue] Processed: ${successCount} success, ${failedCount} failed`);
  return { success: successCount, failed: failedCount };
}
